/*
  Open a product video full screen, with sound, however the shopper reaches it.

  Dawn's own behaviour is two steps: tapping the thumbnail swaps the main
  image for the video's poster, and the shopper then has to find and tap the
  play button on top of it. On a phone that second tap plays the video at
  gallery height, letterboxed and silent. One tap, full screen, audible, is
  what the gallery is for.

  Three things make this more than a one-liner.

  Sound. Shopify renders these with muted="muted" -- it has to, because a
  video that autoplays cannot carry audio unless the browser has been given a
  user gesture to hang it on. A tap is such a gesture, so the mute can be
  lifted at that moment; arriving by any other route it has to stay on, or
  the browser refuses to play the video at all. unmute() therefore only runs
  from a real tap, and puts the mute back if playback is refused anyway.

  Timing. Dawn binds its handler to the thumbnail's own button, so this
  listener is delegated to the gallery and runs on the way back up -- by which
  point setActiveMedia has already swapped the slide and called loadContent,
  and the <video> exists to be addressed.

  iOS. Safari on iPhone does not implement the standard Fullscreen API on
  arbitrary elements; it exposes webkitEnterFullscreen on the video element
  alone, and only once the video has metadata. Dawn renders these with
  preload="none", so at the moment of the tap there is nothing loaded to go
  full screen with: the request has to wait for loadedmetadata and fire then,
  while the gesture is still warm.
*/
(function () {
  'use strict';

  var FS_WAIT_MS = 4000;
  var CONTROL_BAR_PX = 48; // native controls sit along the bottom edge

  function enterFullscreen(video) {
    if (document.fullscreenElement || document.webkitFullscreenElement) return true;
    // iOS exposes this on the video element only, and ignores the standard API
    if (typeof video.webkitEnterFullscreen === 'function') {
      try {
        video.webkitEnterFullscreen();
        return true;
      } catch (e) {
        /* fall through to the standard API */
      }
    }
    var request =
      video.requestFullscreen ||
      video.webkitRequestFullscreen ||
      video.mozRequestFullScreen ||
      video.msRequestFullscreen;
    if (request) {
      try {
        var result = request.call(video);
        if (result && typeof result.catch === 'function') result.catch(function () {});
        return true;
      } catch (e) {
        /* a browser that refuses simply keeps playing inline */
      }
    }
    return false;
  }

  /*
    Lift the mute Shopify sets, and play.

    Only ever called from a tap. If the browser still refuses -- some will,
    depending on how it scores the gesture -- the mute goes back on and the
    video plays silently, which is what it did before this file existed.
    Better a silent video than a dead one.
  */
  function unmuteAndPlay(video) {
    video.removeAttribute('muted');
    video.muted = false;
    video.volume = 1;
    var played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(function () {
        video.muted = true;
        var retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(function () {});
      });
    }
  }

  /*
    A click on a <video> that carries native controls also toggles play/pause
    in the browser itself. On the tap that opens full screen that toggle lands
    after our play() and cancels it, leaving a silent paused video in full
    screen -- so confirm playback once the dust settles. Only on the opening
    tap: once full screen is up, this handler stands aside and pause/play
    belong to the controls again.
  */
  function ensurePlaying(video) {
    window.setTimeout(function () {
      if (!video.paused) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }, 150);
  }

  /*
    Get the video playing, whatever else happens.

    Muted playback is the one thing every mobile browser allows without a
    gesture, and it has to come first. Asking for sound first looked harmless
    and was not: the refused play() never starts the download, and because
    Dawn renders these with preload="none" there is then no metadata, so the
    loadedmetadata handler never runs and the shopper is left looking at the
    black poster. That is the swipe bug.
  */
  function playMuted(video) {
    video.muted = true;
    markLive(video);
    // older iOS needs both spellings to keep playback inline rather than
    // bouncing straight to its own fullscreen player
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
    dropPosterWhenPlaying(video);
  }

  /*
    Tell the stylesheet this video is running.

    Dawn's own reveal depends on .is-active landing under a .product--thumbnail
    ancestor below 750px; when any part of that lags, the video plays behind
    its preview image. This marks the container directly so the CSS can show
    it without waiting on any of that.
  */
  function markLive(video) {
    reveal(video.closest('.product-media-container'));
  }

  /* Takes either a slide or the media container itself. */
  function reveal(node) {
    if (!node) return;
    var container = node.classList && node.classList.contains('product-media-container')
      ? node
      : node.querySelector('.product-media-container.media-type-video, ' +
                           '.product-media-container.media-type-external_video');
    if (container) container.classList.add('fas-video-live');
  }

  /*
    Drop the poster once frames are really arriving.

    A <video> shows its poster until it paints, and on iOS one that was
    started out of sight can stay on it indefinitely. Once the clock has
    actually moved there are frames to show, so the poster has no job left
    and removing it guarantees the video surface is what is on screen.
  */
  function dropPosterWhenPlaying(video) {
    if (!video || video.dataset.posterDropped) return;
    var drop = function () {
      if (video.currentTime <= 0) return;
      video.dataset.posterDropped = '1';
      video.removeAttribute('poster');
      video.removeEventListener('timeupdate', drop);
    };
    video.addEventListener('timeupdate', drop);
    drop();
  }


  function playFullscreen(video, gesture) {
    if (!video || video.dataset.fullscreenRequested === 'pending') return;

    // pixels first, always
    playMuted(video);
    // only a real tap has earned the right to ask for sound
    if (gesture) unmuteAndPlay(video);

    // metadata already in hand: go now, inside the gesture
    if (video.readyState >= 1 && enterFullscreen(video)) {
      ensurePlaying(video);
      return;
    }

    // preload="none" means there is nothing to show yet; go as soon as there is
    video.dataset.fullscreenRequested = 'pending';
    var done = false;
    var onReady = function () {
      if (done) return;
      done = true;
      delete video.dataset.fullscreenRequested;
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
      if (gesture && video.muted) unmuteAndPlay(video);
      enterFullscreen(video);
      ensurePlaying(video);
    };
    video.addEventListener('loadedmetadata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
    // if the file never reports in, stop waiting and leave it playing inline
    window.setTimeout(function () {
      if (done) return;
      done = true;
      delete video.dataset.fullscreenRequested;
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
    }, FS_WAIT_MS);
  }

  /* The <video> is created by DeferredMedia the moment the slide becomes
     active, but a slow upgrade can put that a tick behind this handler, so
     give it a couple of frames before giving up. */
  function withVideo(container, gesture, attempt) {
    if (!container) return;

    /*
      Reveal before anything is created.

      Dawn's loadContent builds the <video> and, because Shopify sets
      autoplay, starts it the same instant -- and up to now that happened
      while .deferred-media was still display:none. Desktop engines repaint
      once the element appears; iOS does not. A video that began playing
      inside a hidden box there never paints a frame and goes on showing its
      poster for good, which is the "plays but shows the thumbnail" everyone
      kept seeing. So the container is marked live first, and the video is
      born visible.
    */
    reveal(container);

    var video = container.querySelector('video');
    if (video) {
      playFullscreen(video, gesture);
      return;
    }
    var deferred = container.querySelector('.deferred-media');
    if (deferred && typeof deferred.loadContent === 'function' && !deferred.getAttribute('loaded')) {
      deferred.loadContent(false);
    }
    if ((attempt || 0) < 8) {
      window.requestAnimationFrame(function () {
        withVideo(container, gesture, (attempt || 0) + 1);
      });
    }
  }

  function slideFor(gallery, target) {
    var viewer = gallery.querySelector('[id^="GalleryViewer"]') || gallery;
    return (
      (target && viewer.querySelector('[data-media-id="' + target + '"]')) ||
      viewer.querySelector('.product__media-item.is-active') ||
      viewer.querySelector('.is-active')
    );
  }

  function onGalleryClick(event) {
    var gallery = event.currentTarget;

    // 1. the small thumbnail
    var item = event.target.closest('.thumbnail-list__item[data-media-type="video"]');
    if (item && event.target.closest('button')) {
      withVideo(slideFor(gallery, item.dataset.target), true);
      return;
    }

    // 2. the main image itself, whether it is still the poster or already
    //    the playing video -- the shopper who swiped here rather than
    //    tapping a thumbnail expects the same thing to happen
    var container = event.target.closest('.product-media-container.media-type-video');
    if (!container) return;

    var video = container.querySelector('video');
    if (video) {
      /* Leave the native control bar alone: a tap on the scrubber or the
         volume slider is aimed at the controls, not at the video. */
      var box = video.getBoundingClientRect();
      if (event.clientY > box.bottom - CONTROL_BAR_PX) return;
      if (document.fullscreenElement || document.webkitFullscreenElement) return;
    }
    withVideo(container, true);
  }

  /*
    Arriving by swipe.

    Dawn's own slideChanged handler only re-highlights the thumbnail -- it
    never loads the media -- so without this the shopper who swipes to the
    video sits looking at its poster. Getting it playing is the job here, and
    playFullscreen starts muted precisely so that this always succeeds.

    Sound and full screen are a separate question. A swipe is not a click and
    browsers do not treat scrolling as activation, so both are asked for and
    may be refused. onTouchEnd below is the second attempt.
  */
  var slideTimer = null;

  /*
    Make the slide the shopper swiped to the active one.

    This is the reason a swiped-to video played but could not be seen. Below
    750px the theme hides .deferred-media outright and only shows it again for
    a slide carrying .is-active -- and Dawn's own slideChanged handler just
    re-highlights the thumbnail, it never marks the slide active. So the video
    loaded, played and sent out audio from inside a display:none element, with
    the still preview image left sitting on top of it. A thumbnail with sound.

    Handing the swipe to the gallery's own setActiveMedia fixes it properly:
    that is the method the thumbnails already call, and it sets the class,
    moves the thumbnail highlight and pauses whatever was playing before.
  */
  function makeActive(viewer, slide) {
    viewer.querySelectorAll('.product__media-item').forEach(function (el) {
      if (el !== slide) {
        el.querySelectorAll('.fas-video-live').forEach(function (c) {
          c.classList.remove('fas-video-live');
        });
      }
    });
    var gallery = viewer.closest('media-gallery');
    var id = slide.dataset.mediaId;
    if (gallery && typeof gallery.setActiveMedia === 'function' && id) {
      try {
        gallery.setActiveMedia(id, false);
        return;
      } catch (e) {
        /* fall back to setting the class directly */
      }
    }
    viewer.querySelectorAll('[data-media-id]').forEach(function (el) {
      el.classList.remove('is-active');
    });
    slide.classList.add('is-active');
  }

  function onSlideChanged(event) {
    /* Dawn dispatches this on the viewer with bubbles:false, so the listener
       lives there and currentTarget is the viewer, not the gallery. */
    var viewer = event.currentTarget;
    var slide = slideFor(viewer, event.detail && event.detail.currentElement
      ? event.detail.currentElement.dataset.mediaId
      : null);
    if (!slide) return;
    /* a flick fires this several times on its way past; act on where it lands */
    window.clearTimeout(slideTimer);
    slideTimer = window.setTimeout(function () {
      /* every slide, not only the video: it is what keeps the active state
         honest, so swiping away from the video stops it again */
      makeActive(viewer, slide);
      if (!slide.querySelector('.product-media-container.media-type-video')) return;
      withVideo(slide, false);
      tryAudioOncePlaying(slide);
    }, 200);
  }

  /*
    Ask for sound only once the picture is safely running.

    Lifting the mute up front is what broke the swipe in the first place: the
    refused play() never started the download. Waiting for the video to
    actually be playing means the file is already coming down, so the worst a
    refusal can now do is pause it -- and that is put straight back.
  */
  function tryAudioOncePlaying(slide) {
    var video = slide.querySelector('video');
    if (!video || !video.muted) return;
    var attempt = function () {
      if (!video.muted) return;
      video.muted = false;
      video.volume = 1;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          video.muted = true;
          var again = video.play();
          if (again && typeof again.catch === 'function') again.catch(function () {});
        });
      }
    };
    if (!video.paused && video.readyState >= 2) {
      attempt();
    } else {
      video.addEventListener('playing', attempt, { once: true });
    }
  }

  /*
    The end of the swipe itself.

    This is the one moment on a phone where arriving at the video and a real
    touch coincide, so it is the best chance of the browser granting the
    activation that sound and full screen need. It costs nothing when
    refused, and when the shopper then taps the video deliberately the same
    handler upgrades it.
  */
  function onTouchEnd(event) {
    var gallery = event.currentTarget;
    var viewer = gallery.querySelector('[id^="GalleryViewer"]') || gallery;
    var slide = viewer.querySelector('.product__media-item.is-active') ||
      viewer.querySelector('.is-active');
    if (!slide || !slide.querySelector('.product-media-container.media-type-video')) return;
    var video = slide.querySelector('video');
    // nothing to upgrade once it is already loud or already expanded
    if (!video || (!video.muted && !video.paused)) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) return;
    unmuteAndPlay(video);
    if (video.readyState >= 1) enterFullscreen(video);
  }

  function bind(root) {
    root.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (gallery.dataset.fullscreenBound) return;
      gallery.dataset.fullscreenBound = 'true';
      gallery.addEventListener('click', onGalleryClick);
      gallery.addEventListener('touchend', onTouchEnd, { passive: true });
      var viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (viewer) viewer.addEventListener('slideChanged', onSlideChanged);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bind(document);
    });
  } else {
    bind(document);
  }

  // variant changes and quick-add re-render the gallery
  document.addEventListener('shopify:section:load', function (e) {
    bind(e.target);
  });
})();
